import {
  type DepositStatus,
  type SwapStatus,
  TransactionsHeader,
  TransactionsTable,
  type TransactionData,
  transactionExportToCSV,
  type FilterStatus,
  useInterval,
} from '@valiant/ui-shared'
import { environment } from '@/environment'
import {
  useGetTransactionsQuery,
  useApproveDepositMutation,
  useRejectDepositMutation,
  useApproveSwapMutation,
  useRejectSwapMutation,
  type TransactionStatus,
  type TransactionType,
} from '@/generated/graphql'
import { useState, useMemo, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/AuthProvider'

interface TransactionsContentProps {
  debouncedSearch: string
  selectedStatus: TransactionStatus | DepositStatus | SwapStatus | null
  selectedType: TransactionType | null
  isFlagged: boolean | null
  groupId?: string
  companyId?: string
  onFilter: (
    status: FilterStatus | null,
    type: TransactionType | null,
    flagged?: boolean | null,
  ) => void
}

export function TransactionsContent({
  debouncedSearch,
  selectedStatus,
  selectedType,
  isFlagged,
  groupId,
  companyId,
  onFilter,
}: TransactionsContentProps) {
  const navigate = useNavigate()
  // const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const filter = useMemo(() => {
    interface FilterObject {
      search?: string
      status?: TransactionStatus //Forced type until we remove transaction status
      type?: TransactionType
      groupId?: string
      companyId?: string
      isFlagged?: boolean
    }

    const filterObj: FilterObject = {}
    if (debouncedSearch.trim()) filterObj.search = debouncedSearch.trim()
    if (selectedStatus) filterObj.status = selectedStatus as TransactionStatus
    if (selectedType) filterObj.type = selectedType
    if (isFlagged !== null) filterObj.isFlagged = isFlagged
    if (groupId) filterObj.groupId = groupId
    if (companyId) filterObj.companyId = companyId
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, selectedStatus, selectedType, isFlagged, groupId, companyId])

  // Pagination state
  const [pagination, setPagination] = useState<{
    first?: number
    after?: string
    last?: number
    before?: string
  }>({
    first: 20,
  })

  const [
    { data: transactionsData, fetching: transactionsLoading, error: _transactionsError },
    refetchTransactions,
  ] = useGetTransactionsQuery({
    variables: { filter, pagination },
    requestPolicy: 'network-only',
  })

  // Track whether the initial fetch has completed to avoid flickering on background refetches
  const hasLoadedRef = useRef(false)
  if (!transactionsLoading) hasLoadedRef.current = true

  // Auto-refetch every 10 seconds to catch status changes, without showing loading state
  useInterval(() => {
    refetchTransactions({ requestPolicy: 'network-only' })
  }, 10000)

  // State for manual refresh loading
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)

  const [, approveDeposit] = useApproveDepositMutation()
  const [, rejectDeposit] = useRejectDepositMutation()
  const [, approveSwap] = useApproveSwapMutation()
  const [, rejectSwap] = useRejectSwapMutation()

  // Reset pagination when filters or search change
  useEffect(() => {
    setPagination({ first: 20 })
  }, [debouncedSearch, selectedStatus, selectedType, isFlagged])

  const transactions: TransactionData[] = transactionsData?.getTransactions?.nodes || []

  // Pagination handlers
  const handleNextPage = (cursor: string) => {
    setPagination({ first: 20, after: cursor })
  }

  const handlePreviousPage = (cursor: string) => {
    setPagination({ last: 20, before: cursor })
  }

  // Transaction handlers
  const handleViewDetails = (transactionId: string) => {
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) return

    if (transaction.type === 'DEPOSIT') {
      navigate(`/transactions/deposit/${transactionId}`)
    } else if (transaction.type === 'TRADE') {
      navigate(`/transactions/swap/${transactionId}`)
    }
  }

  const handleApproveConfirm = async (transactionId: string) => {
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) return

    if (isComplianceUser && transaction.type !== 'DEPOSIT') return

    try {
      if (transaction.type === 'DEPOSIT') {
        const result = await approveDeposit({ depositId: transactionId })
        if (result.error) {
          throw new Error(result.error.message)
        }
      } else if (transaction.type === 'TRADE') {
        const result = await approveSwap({ input: { swapId: transactionId } })
        if (result.error) {
          throw new Error(result.error.message)
        }
      } else {
        toast.error('Unknown transaction type')
        return
      }
      toast.success('Transaction approved successfully')
    } catch (error) {
      toast.error('Failed to approve transaction')
      console.error('Error approving transaction:', error)
    }
  }

  const handleRejectConfirm = async (transactionId: string) => {
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) return

    if (isComplianceUser && transaction.type !== 'DEPOSIT') return

    try {
      if (transaction.type === 'DEPOSIT') {
        const result = await rejectDeposit({
          depositId: transactionId,
          reason: 'Rejected by admin',
        })
        if (result.error) {
          throw new Error(result.error.message)
        }
      } else if (transaction.type === 'TRADE') {
        const result = await rejectSwap({
          input: { swapId: transactionId, reason: 'Rejected by admin' },
        })
        if (result.error) {
          throw new Error(result.error.message)
        }
      } else {
        toast.error('Unknown transaction type')
        return
      }
    } catch (error) {
      toast.error('Failed to reject transaction')
      console.error('Error rejecting transaction:', error)
    }
  }

  const exportToCSV = () => {
    transactionExportToCSV(transactions, environment.MSB)
  }

  const handleManualRefresh = () => {
    setIsManualRefreshing(true)
    try {
      refetchTransactions({ requestPolicy: 'network-only' })
      toast.success('Transactions refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh transactions')
      console.error('Error refreshing transactions:', error)
    } finally {
      setIsManualRefreshing(false)
    }
  }

  return (
    <div className="space-y-4">
      <TransactionsHeader
        selectedStatus={selectedStatus}
        selectedType={selectedType}
        isFlagged={isFlagged}
        onFilter={onFilter}
        onExportCSV={exportToCSV}
        onRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
        showExportButton={true}
        showRefreshButton={true}
      />

      <TransactionsTable
        transactions={transactions}
        loading={transactionsLoading && !hasLoadedRef.current}
        onApproveTransaction={handleApproveConfirm}
        onRejectTransaction={handleRejectConfirm}
        onViewTransactionDetails={handleViewDetails}
        showActionButtons={true}
        showFlagColumn={true}
        isComplianceUser={isComplianceUser}
        // Pagination props
        pageInfo={transactionsData?.getTransactions?.pageInfo}
        totalCount={transactionsData?.getTransactions?.totalCount || 0}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        showPagination={true}
      />
    </div>
  )
}
